import { format } from 'date-fns';

import { Contract } from '../validationSchemas/ContractSchema';
import { removeCommas, removeCommasAndOmitCents } from './helpers';
import { snakeCaseKeys } from './object';

const mapFormToContract = (data: Contract) => {
  const returnValue = {
    ...snakeCaseKeys(data),
    id: data.contractId || null,
    client_id: data.selectedClientId,
    advertiser_id: data.selectedAdvertiserId,
    name: data.contractName,
    start_date: format(data.startDate, 'yyyy-MM-dd'), // may need resolved to .000 ms places // formatISO before sending to API
    end_date: format(data.endDate, 'yyyy-MM-dd'), // may need resolved to .000 ms places // formatISO before sending to API
    advisory_budget: removeCommas(data.advisoryBudget),
    bonus: removeCommasAndOmitCents(data.bonus),
    io_path: data.ioFile,
    count_paused_campaigns: 0,
    has_unpaused_campaigns: false,
    complete: data.isComplete,
    cost_incurring_features: [], // 'placed'
    errors: [],
    is_pmp_contract: false, // TODO: what affects this?
    store_list: data.storeList?.map((item) => (item as any).label || item).join(', '),
    // kpi_goal needs to be in this format: "lorem:ipsum;asdf:qwer;"
    kpi_goal:
      data.kpiGoalFieldArray &&
      Object.values(data.kpiGoalFieldArray)
        .map(({ key, value }) => `${key}:${value};`)
        .join(''),
    uses_cpc: undefined,
    contract_cpc: undefined,
    contract_cpm: (data.contractCpm || '0').toString(),
    contract_cpa: (data.contractCpc || '0').toString(),
    cpa_action: data.cpaAction?.join(','),
    contract_lci_campaigns_attributes: Array.isArray(data.contractLciCampaigns) ? data.contractLciCampaigns : [],
    contract_kpis_attributes: data.contractKpis?.map((kpi, index) => ({
      id: kpi.id,
      kpi_name: kpi.kpiName,
      provider: kpi.provider || null,
      goal_value: typeof kpi.goalValue === 'string' ? parseFloat(kpi.goalValue) : kpi.goalValue,
      goal_format: kpi.goalFormat,
      position: index,
      // eslint-disable-next-line no-underscore-dangle
      _destroy: kpi._destroy || false,
      // eslint-disable-next-line no-underscore-dangle
    })).filter(kpi => !kpi._destroy || kpi.id) || [],
    // conditionally included fields
    frequency_capping: data.frequencyCapEnabled ? data.frequencyCapping : null,
    max_user_views_per_period: data.frequencyCapEnabled ? data.maxUserViewsPerPeriod : null,
    click_frequency_capping: data.performanceCapEnabled ? data.clickFrequencyCapping : null,
    max_user_clicks_per_period: data.performanceCapEnabled ? data.maxUserClicksPerPeriod : null,
    custom_billing_options: data.customBillingOptions?.map((option) => ({
      ...snakeCaseKeys(option),
      start_date: option.startDate && format(option.startDate, 'yyyy-MM-dd'),
      end_date: option.endDate && format(option.endDate, 'yyyy-MM-dd'),
    })),
  };

  // conditionally included key in payload if bidType === 'cpc'
  // this is
  if (data.bidType === 'CPC') {
    returnValue.uses_cpc = true;
    returnValue.contract_cpc = Number(removeCommas(data.pricingRate || '0')) * 100;
  } else if (data.bidType === 'CPM') {
    returnValue.contract_cpm = Number(removeCommas(data.pricingRate || '0')) * 100;
  } else if (data.bidType === 'CPA') {
    // CAUTION: possibly not yet implemented in API!
    returnValue.contract_cpa = Number(removeCommas(data.pricingRate || '0')) * 100;
  } else if (data.bidType === 'CPV') {
    returnValue.contract_cpm = Number(removeCommas(data.pricingRate || '0')) * 100;
  }
  // CAUTION: there may be more possible entries
  // to the cost_incurring_features array
  if (data.placed) {
    returnValue.cost_incurring_features = ['placed'];
  }

  // Legacy scalar lci
  if (data.lciMeasurement) {
    const firstActive = (returnValue.contract_lci_campaigns_attributes || []).find(
      (r: any) => r?.lci_campaign_id != null && r.active !== false
    );
    (returnValue as any).lci_campaign_id = firstActive?.lci_campaign_id ?? null;
  } else {
    (returnValue as any).lci_campaign_id = null;
    (returnValue as any).contract_lci_campaigns_attributes = [];
  }

  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete (returnValue as any).contract_lci_campaigns;
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete (returnValue as any).contract_kpis;

  // Remove undefined and NaN values to prevent API errors
  Object.keys(returnValue).forEach((key) => {
    const value = returnValue[key];
    if (value === undefined || (typeof value === 'number' && Number.isNaN(value))) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete returnValue[key];
    }
  });

  return returnValue;
};

export default mapFormToContract;
